/*
 * Authors: mariushirjanu
 * Created: 03/02/2023
 */
import * as _ from 'underscore';
import { ICartItemStatus, ICatalogCartItem, IPricesRequest, IPricesResponse, IPricesResponseItem, IPunchoutCartItem, PriceCartService } from '../../cart/price-cart.service';
import { Injectable } from '@angular/core';
import { OrderEntryService } from '../order-entry.service';
import { util } from '../../../../shared/util';

export interface IPunchout {
  sourceType:string;
  extCurrency:string;
  catalogId:number;
  items:IPunchoutItem[];
}

export interface IPunchoutItem {
  quantity?:number;
  extUnitPrice:number;
  title:string;
  description:string;
  shortDescription:string;
  part:string;
  vendorId:number;
  category?:string;
  oppositePriceConversion?:boolean;
  externalUNSPSCCode?:string;
}

@Injectable()
export class PunchoutCatalogService {
  private punchoutHandler:(...args:any[]) => Promise<any>;
  private _partNumber:string;

  constructor(
    private priceCartService:PriceCartService,
    private orderEntryService:OrderEntryService
  ) {
  }

  // ask for price before to add the punchout items to the cart
  public punchoutToCart(punchoutData:IPunchout):Promise<any> {

    // get items
    const cartItems:ICatalogCartItem[] | IPunchoutCartItem[] = <ICatalogCartItem[] | IPunchoutCartItem[]>this.priceCartService.preparedCartStatus();
    const punchoutItems:IPunchoutCartItem[] = this.preparePunchoutItems(punchoutData);
    _.each(punchoutItems, (punchoutItem:IPunchoutCartItem) => {
      punchoutItem.itemId = this.priceCartService.getItemId(punchoutItem);
      return punchoutItem;
    });

    // merge the items from cart with punchout
    const mergedItems:IPunchoutCartItem[] | ICatalogCartItem[] = this.mergeItems(cartItems, punchoutItems);

    // get prices
    const pricesDataRequest:IPricesRequest = this.priceCartService.createPriceRequest(this.orderEntryService.order?.id, mergedItems);

    return this.priceCartService.loadPricesFromServer(pricesDataRequest)
      .then((prices:IPricesResponse) => {
        // add to cart and set the new prices
        this.transferToCart(mergedItems, prices);
        this.priceCartService.prices = prices;

        return prices;
      });
  }

  public punchoutToCartHandler(handler?:(...args:any[]) => Promise<any>):(...args:any[]) => Promise<any> {
    if (arguments.length) {
      this.punchoutHandler = handler;
    }

    return this.punchoutHandler;
  }

  public get punchoutPartNumber():string {
    return this._partNumber;
  }

  public set punchoutPartNumber(partNumber:string) {
    this._partNumber = partNumber;
  }

  private preparePunchoutItems(punchoutData:IPunchout):IPunchoutCartItem[] {
    // get only the attributes required for punchout items
    const punchoutItems:IPunchoutCartItem[] = [];
    _.each(punchoutData.items, (punchoutItem:IPunchoutItem) => {
      const item:IPunchoutCartItem = <IPunchoutCartItem>punchoutItem;
      item.catalogId = punchoutData.catalogId;
      item.sourceType = punchoutData.sourceType;
      item.extCurrency = punchoutData.extCurrency;

      punchoutItems.push(<IPunchoutCartItem>this.priceCartService.prepareCartStatusItems(item, punchoutItem.quantity));
    });

    return punchoutItems;
  }

  // transfer the items to cart
  private transferToCart(items:ICatalogCartItem[] | IPunchoutCartItem[], prices:IPricesResponse):void {
    const pricesItems:IPricesResponseItem[] = util.copyAndExtend(prices.items);
    _.each(pricesItems, (pricesItem:IPricesResponseItem) => {
      pricesItem.itemId = this.priceCartService.getItemId(<any>pricesItem);
      return pricesItem;
    });

    _.each(items, (item:ICatalogCartItem | IPunchoutCartItem) => {
      if (item.sourceType && item.sourceType === PriceCartService.SOURCE_PUNCHOUT) {
        // Punchout Items
        const selectedItem:IPricesResponseItem = _.findWhere(pricesItems, { itemId: item.itemId });
        if (selectedItem) {
          item.quantity = selectedItem.quantity;
          (<IPunchoutCartItem>item).unitPrice = selectedItem.unitPrice;
          (<IPunchoutCartItem>item).title = selectedItem.description;
          if (selectedItem.externalUNSPSCCode) {
            (<IPunchoutCartItem>item).externalUNSPSCCode = selectedItem.externalUNSPSCCode;
          }
          // se the correct order line number and remove id
          if (selectedItem.id) {
            (<IPunchoutCartItem>item).orderLineNumber = selectedItem.id;
          }
          delete ((<ICatalogCartItem>item).id);
        }
      } else {
        // Catalog Items
        const cartSelectedItem:ICartItemStatus = _.findWhere(this.priceCartService.cartStatus, { itemId: item.itemId });
        if (cartSelectedItem && cartSelectedItem.product) {
          const backupItem:ICatalogCartItem | IPunchoutCartItem = util.copyAndExtend(item);
          item = <any>cartSelectedItem.product;
          item.quantity = backupItem.quantity;
          item.unitPrice = (<any>cartSelectedItem.product).unitPrice || (<any>cartSelectedItem.product).initialUnitPrice;
          item.title = (<any>cartSelectedItem.product).title || backupItem.title;
        }
      }

      this.priceCartService.addToCart(item, item.quantity);
    });
  }

  // merge cart items with punchout items and remove duplicates
  private mergeItems(cartItems:ICatalogCartItem[] | IPunchoutCartItem[], punchoutItems:IPunchoutCartItem[]):ICatalogCartItem[] | IPunchoutCartItem[] {
    const mergedItems:ICatalogCartItem[] | IPunchoutCartItem[] = _.values(util.copyAndExtend(cartItems));
    _.each(punchoutItems, (punchoutItem:IPunchoutCartItem, idx:number) => {
      const itemIdFoundInCart:number = _.findIndex(mergedItems, (item:ICatalogCartItem | IPunchoutCartItem) => item.itemId === punchoutItem.itemId);
      if (itemIdFoundInCart !== -1) {
        // in case that a second punchout is performed, use the items received from punchout
        mergedItems[ itemIdFoundInCart ] = punchoutItems[ idx ];

        // restore the id (the order line number) from the cart
        if (!mergedItems[ itemIdFoundInCart ].id) {
          mergedItems[ itemIdFoundInCart ].id = cartItems[ itemIdFoundInCart ].id;
        }
      } else {
        (<IPunchoutCartItem[]>mergedItems).push(punchoutItem);
      }
    });
    return mergedItems;
  }
}